import { cn } from '@ifixit/ui/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { useBreakpoint, useBreakpointValue } from '@ifixit/ui/hooks/use-media-query';
import { useTranslations } from '@ifixit/i18n/hooks';
import { type Money, shouldShowProductRating } from '@ifixit/helpers';
import { FaIcon } from '@ifixit/ui/components/icon';
import { ___p, _js } from '@ifixit/utils/localize';
import { Rating } from '@ifixit/product-components';
import { type CartLineItem } from '@ifixit/shopify-cart-sdk';
import { useAddToCart } from '@ifixit/shopify-cart-sdk/hooks';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { UserPriceTag } from 'Shared/user-price-tag';
import { fireGlobal } from 'Shared/utils/global-events';
import {
   Dispatch,
   Fragment,
   MutableRefObject,
   SetStateAction,
   useLayoutEffect,
   useRef,
   useState,
   type JSX,
} from 'react';
import { useHistoryState } from './HistoryState';
import { PlaceholderImage } from './PlaceholderImage';
import { QuantityBadge } from './QuantityBadge';
import { LocaleCode } from '@ifixit/i18n/types';

export interface GuideProductsData {
   kits: GuideProduct[];
   parts: GuideProduct[];
   tools: GuideProduct[];
}

interface GuideProductVariant {
   name: string;
   proPricesByTier: Record<string, Money>;
   totalPrice: string;
   cartLine: CartLineItem;
   isInStock: boolean;
}

export interface GuideProduct {
   name: string;
   sku: string | null;
   totalPrice: string | null;
   quantity: number;
   notes: string | null;
   type: string | null;
   productUrl: string;
   wikiUrl: string;
   imageUrl: string | null;
   highResImageUrl: string | null;
   isOptional: boolean;
   isExternal: boolean;
   isForSale: boolean;
   supplierText: string | null;
   rating: number | null;
   ratingCount: number;
   variants: GuideProductVariant[];
   initialVariant: GuideProductVariant | null;
   localeCode: LocaleCode;
}
type PartOrTool = 'Part' | 'Tool' | 'Kit';

function useElementSize(ref: MutableRefObject<HTMLElement | undefined>) {
   const [size, setSize] = useState<{ width: number; height: number } | undefined>();

   useLayoutEffect(() => {
      const element = ref.current;
      if (!element) {
         return;
      }

      const observer = new ResizeObserver(entries => {
         const rect = entries[0]?.contentRect;
         if (rect) {
            setSize({ width: rect.width, height: rect.height });
         }
      });
      observer.observe(element);

      return () => observer.disconnect();
   }, [ref]);

   return size;
}

function ProductItem({
   name,
   sku,
   quantity,
   notes,
   type,
   productUrl,
   imageUrl,
   highResImageUrl,
   isExternal,
   isForSale,
   supplierText,
   rating,
   ratingCount,
   variants,
   initialVariant,
   localeCode,
   addToCartText,
   viewProductText,
   wrapperClassName,
   imageClassName,
   partOrTool,
   isOptional,
}: GuideProduct & {
   addToCartText: string;
   viewProductText: string;
   wrapperClassName: string;
   imageClassName: string;
   partOrTool: PartOrTool;
}) {
   const openInNewTab = true;
   const isAboveMd = useBreakpoint('md');
   const isTablet = isAboveMd === false;
   const [selectedVariant, setSelectedVariant] = useState<GuideProductVariant | null>(
      initialVariant
   );
   const url = selectedVariant?.cartLine.url ?? productUrl;

   const title = type ? `${name} (${type})` : name;
   const escapedDashTitle = title.replace('-', '\u2011');

   const onProductClick = () => {
      trackAnalyticsCustomEvent({
         eventCategory: 'Guide Products',
         eventAction: `Guide Products - Click Product - ${partOrTool}${
            isExternal ? ' - External' : ''
         }`,
         eventName: isExternal ? `external:${name}:${url}` : `ifixit:${name}:${sku}`,
      });
   };

   const openSettings = openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {};

   return (
      <li
         className={cn('flex flex-col gap-2 w-full items-stretch p-3', wrapperClassName)}
         onClick={onProductClick}
      >
         <div className="flex gap-2 items-stretch shrink-0">
            <div className="relative mr-1.5 flex self-start">
               <a
                  href={url}
                  {...openSettings}
                  aria-hidden="true"
                  tabIndex={-1}
                  className="self-start flex visited:text-gray-700 [&:hover_img]:border [&:hover_img]:border-gray-300 [&:hover_img]:rounded"
               >
                  {imageUrl ? (
                     <img
                        className={cn(
                           'self-start block object-cover border border-gray-300 rounded',
                           imageClassName
                        )}
                        src={imageUrl}
                        srcSet={`${imageUrl} 1x, ${highResImageUrl} 2x`}
                        alt={name}
                     />
                  ) : (
                     <PlaceholderImage className={imageClassName} />
                  )}
               </a>
               {quantity > 1 && <QuantityBadge quantity={quantity} />}
            </div>
            <div className="flex flex-col gap-1 flex-1 items-start">
               <a
                  href={url}
                  {...openSettings}
                  className="title text-base font-semibold text-gray-900 visited:text-gray-900 hover:text-gray-700 hover:no-underline focus:text-gray-700 focus:no-underline"
               >
                  {escapedDashTitle}
               </a>
               <VariantSelector
                  productName={name}
                  variants={variants}
                  selected={selectedVariant}
                  setSelected={setSelectedVariant}
               />
               {selectedVariant && isForSale && variants.length < 2 && (
                  <a href={url} {...openSettings} className="hover:no-underline">
                     <UserPriceTag
                        price={selectedVariant.cartLine.price}
                        compareAtPrice={selectedVariant.cartLine.compareAtPrice}
                        proPricesByTier={selectedVariant.proPricesByTier}
                        showDiscountPercentage={false}
                        className="price-tag-xs price-tag-subdued"
                     />
                  </a>
               )}
               {rating != null && <ProductReviews rating={rating} count={ratingCount} />}
               <ProductBadges
                  isOptional={isOptional}
                  isExternal={isExternal}
                  supplierText={supplierText}
                  url={url}
                  openInNewTab={openInNewTab}
               />
               {isExternal && supplierText && (
                  <a
                     href={url}
                     {...openSettings}
                     className="text-base text-amber-600 visited:text-amber-600"
                  >
                     {supplierText}
                  </a>
               )}
               {notes && (
                  <a href={url} {...openSettings} className="text-sm visited:text-gray-700">
                     {notes}
                  </a>
               )}
            </div>
         </div>
         <div className="flex justify-end buy-buttons">
            <BuyButton
               buttonSize={isTablet ? 'sm' : 'md'}
               isForSale={isForSale}
               isExternal={isExternal}
               openInNewTab={openInNewTab}
               productUrl={url}
               addToCartText={addToCartText}
               viewProductText={viewProductText}
               partOrTool={partOrTool}
               cartLine={selectedVariant?.cartLine ?? null}
               isInStock={selectedVariant?.isInStock ?? false}
               localeCode={localeCode}
            />
         </div>
      </li>
   );
}

function VariantSelector({
   productName,
   variants,
   selected,
   setSelected,
}: {
   productName: string;
   variants: GuideProductVariant[];
   selected: GuideProductVariant | null;
   setSelected: (_: GuideProductVariant | null) => void;
}) {
   if (variants.length < 2 || !selected) {
      return null;
   }
   const inStockVariants = variants.filter(v => v.isInStock);
   const outOfStockVariants = variants.filter(v => !v.isInStock);
   const soldOut = _js('Sold out');
   return (
      <select
         aria-label={_js('Options for %1', productName)}
         value={selected.name}
         onChange={event => {
            const newSelection = inStockVariants.find(
               variant => variant.name === event.target.value
            );
            setSelected(newSelection ?? null);
         }}
         className="w-full h-10 px-3 overflow-hidden text-ellipsis bg-white border border-gray-200 rounded-md"
      >
         {inStockVariants.map(variant => (
            <option key={variant.name} value={variant.name} disabled={false}>
               {variant.totalPrice} {variant.name}
            </option>
         ))}
         {outOfStockVariants.map(variant => (
            <option key={variant.name} value={variant.name} disabled={true}>
               {variant.totalPrice} {variant.name} ({soldOut})
            </option>
         ))}
      </select>
   );
}

type ProductReviewsProps = {
   rating: NonNullable<GuideProduct['rating']>;
   count: GuideProduct['ratingCount'];
};
function ProductReviews({ rating, count }: ProductReviewsProps) {
   const t = useTranslations('ProductPage');
   if (!shouldShowProductRating({ rating, count })) {
      return null;
   }
   return (
      <div className="flex items-center gap-2">
         <Rating value={rating} ariaLabel={t('sections.reviews.ratingStars', { rating })} />
         <p className="m-0 mt-1 text-gray-600" aria-hidden>
            {rating}
         </p>
      </div>
   );
}

function ProductBadges({
   isOptional,
   isExternal,
   supplierText,
   url,
   openInNewTab,
}: {
   isOptional: boolean;
   isExternal: boolean;
   supplierText: string | null;
   url: string;
   openInNewTab: boolean;
}) {
   const hasAffiliateLink =
      isExternal &&
      supplierText &&
      (supplierText.includes('Ebay') || supplierText.includes('Amazon'));
   const hasBadge = isOptional || hasAffiliateLink;
   if (!hasBadge) {
      return null;
   }

   return (
      <div className="flex flex-wrap gap-1.5">
         {isOptional && <OptionalBadge url={url} openInNewTab={openInNewTab} />}
         {hasAffiliateLink && <AffiliateBadge />}
      </div>
   );
}

function AffiliateBadge() {
   return (
      <Tooltip>
         <TooltipTrigger asChild>
            <div className={cn(productBadgeClass, 'bg-gray-100 text-gray-800 cursor-default')}>
               <FaIcon name="circle-info" className="size-2.5 mr-1 text-gray-500" />
               {_js('Affiliate link')}
            </div>
         </TooltipTrigger>
         <TooltipContent className="text-sm">
            {_js('iFixit earns commission when you buy through these links.')}
         </TooltipContent>
      </Tooltip>
   );
}

function OptionalBadge({ url, openInNewTab }: { url: string; openInNewTab: boolean }) {
   const openSettings = openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {};
   return (
      <a
         href={url}
         {...openSettings}
         className={cn(
            productBadgeClass,
            'bg-amber-100 text-amber-800 visited:text-amber-700 hover:no-underline'
         )}
      >
         <FaIcon name="circle-info" className="size-2.5 mr-1 text-amber-500" />
         {_js('Optional')}
      </a>
   );
}

const productBadgeClass =
   'flex items-center text-[10px] font-semibold leading-3 normal-case rounded-md border border-gray-300 px-1.5 py-1';

function GuideItemMicrodata({
   partOrTool,
   name,
   quantity,
   productUrl,
   totalPrice,
}: Pick<GuideProduct, 'name' | 'quantity' | 'productUrl' | 'totalPrice'> & {
   partOrTool: PartOrTool;
}) {
   if (partOrTool === 'Part' || partOrTool === 'Kit') {
      return (
         <PartSchema
            name={name}
            quantity={quantity}
            productUrl={productUrl}
            totalPrice={totalPrice}
         />
      );
   }
   return <ToolSchema name={name} quantity={quantity} productUrl={productUrl} />;
}

function PartSchema({
   name,
   quantity,
   productUrl,
   totalPrice,
}: Pick<GuideProduct, 'name' | 'quantity' | 'productUrl' | 'totalPrice'>) {
   return (
      <div itemProp="supply" itemScope itemType="http://schema.org/HowToSupply">
         <meta itemProp="name" content={name} />
         <meta itemProp="requiredQuantity" content={`${quantity}`} />
         <meta itemProp="url" content={productUrl} />
         <meta itemProp="estimatedCost" content={`${totalPrice}`} />
      </div>
   );
}

function ToolSchema({
   quantity,
   productUrl,
   name,
}: Pick<GuideProduct, 'name' | 'quantity' | 'productUrl'>) {
   return (
      <div itemProp="tool" itemScope itemType="http://schema.org/HowToTool">
         <meta itemProp="name" content={name} />
         <meta itemProp="requiredQuantity" content={`${quantity}`} />
         <meta itemProp="url" content={productUrl} />
      </div>
   );
}

function BuyButton({
   isForSale,
   productUrl: url,
   addToCartText,
   viewProductText,
   buttonSize,
   openInNewTab,
   cartLine,
   isInStock,
   localeCode,
}: Pick<GuideProduct, 'isForSale' | 'productUrl' | 'isExternal'> & {
   addToCartText: string;
   viewProductText: string;
   buttonSize: 'sm' | 'md';
   partOrTool: PartOrTool;
   openInNewTab: boolean;
   cartLine: CartLineItem | null;
   isInStock: boolean;
   localeCode: LocaleCode;
}) {
   if (!isForSale) {
      return null;
   }
   const openSettings = openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {};
   const buttonClass = cn(
      'inline-flex items-center justify-center py-0 px-3 h-auto min-h-8 rounded-md border-0 font-normal shrink-0 self-end md:self-start bg-brand-500 hover:bg-brand-600 text-white visited:text-white hover:no-underline transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
      buttonSize === 'sm' ? 'text-sm' : 'text-base'
   );

   if (cartLine && isInStock) {
      return (
         <AddToCartButton
            addToCartText={addToCartText}
            buttonClass={buttonClass}
            cartLine={cartLine}
            localeCode={localeCode}
         />
      );
   }

   return (
      <a href={url} {...openSettings} className={buttonClass}>
         {viewProductText}
      </a>
   );
}

function AddToCartButton({
   addToCartText,
   cartLine,
   localeCode,
   buttonClass,
}: {
   addToCartText: string;
   buttonClass: string;
   cartLine: CartLineItem;
   localeCode: LocaleCode;
}) {
   const { addToCart, enabled } = useAddToCart();
   return (
      <button
         type="button"
         disabled={!enabled}
         className={buttonClass}
         onClick={() => {
            addToCart.mutate({
               analytics: {
                  descriptor: 'Guide Products',
                  localeCode: localeCode,
               },
               lines: [cartLine],
            });
            fireGlobal('cart.addcomplete');
         }}
      >
         {addToCartText}
      </button>
   );
}

function FeaturedProduct(props: GuideProduct & { partOrTool: PartOrTool }) {
   return (
      <ProductItem
         {...props}
         wrapperClassName="bg-brand-50 border-t border-x border-brand-200 first:rounded-t last:rounded-b last:border-b"
         imageClassName="size-16"
         addToCartText={_js('Add to cart')}
         viewProductText={_js('View')}
      />
   );
}

function Product(props: GuideProduct & { partOrTool: PartOrTool }) {
   return (
      <ProductItem
         {...props}
         wrapperClassName="bg-white border-t border-x border-gray-200 first:rounded-t last:rounded-b last:border-b"
         imageClassName="size-12"
         addToCartText={props.isExternal ? _js('View') : _js('Add to cart')}
         viewProductText={_js('View')}
      />
   );
}

function ProductList({
   products,
   heading,
   subheading,
   ItemsAs,
   partOrTool,
   renderMicrodata,
}: {
   products: Array<GuideProduct>;
   heading?: string;
   subheading?: string;
   ItemsAs: (_: GuideProduct & { partOrTool: PartOrTool }) => JSX.Element;
   partOrTool: PartOrTool;
   renderMicrodata: boolean;
}) {
   const productItems = products.map((product: GuideProduct, index) => (
      <Fragment key={index}>
         <ItemsAs {...product} partOrTool={partOrTool} />
      </Fragment>
   ));

   const ItemsMicrodata = renderMicrodata
      ? products.map((product: GuideProduct, index) => (
           <GuideItemMicrodata
              key={index}
              name={product.name}
              quantity={product.quantity}
              productUrl={product.productUrl}
              totalPrice={product.totalPrice}
              partOrTool={partOrTool}
           />
        ))
      : null;

   return (
      <>
         {heading && (
            <p className="m-0 p-0 text-gray-800 text-lg sm:text-xl font-medium [&:not(:first-child)]:pt-4">
               {heading}
            </p>
         )}
         {subheading && <p className="m-0 p-0 text-base font-normal text-gray-500">{subheading}</p>}
         <div className="flex flex-row shrink-0 w-full">
            <ul
               className={`item-list-${partOrTool.toLowerCase()}s flex flex-col items-stretch gap-0 m-0 p-0 pt-3 w-full shrink-0 break-words [&:not(:last-child)]:pb-3`}
            >
               {productItems}
            </ul>
         </div>
         {ItemsMicrodata}
      </>
   );
}

export function GuideProductsSection({
   kits,
   tools,
   parts,
   introRef,
}: GuideProductsData & { introRef: MutableRefObject<HTMLDivElement> }) {
   const isAboveSm = useBreakpoint('sm');
   const isMobile = isAboveSm === false;

   const sizeDims = useElementSize(introRef);

   const heightDiff = sizeDims?.height == null ? 0 : sizeDims.height;
   const sectionCollapsedHeight = isMobile ? 264 : Math.max(heightDiff, 370);

   const buttonHeight = 80;
   const padding = useBreakpointValue<number>({ base: 12, md: 16 });

   const [isExpanded, setIsExpanded] = useHistoryState(false, 'productsExpanded');
   const elementRef = useRef<HTMLDivElement>(undefined);

   useLayoutEffect(() => {
      if (!elementRef.current) {
         return;
      }

      const myHeight = elementRef.current.scrollHeight;
      const expandedByDefault = myHeight < sectionCollapsedHeight + buttonHeight;
      if (expandedByDefault) {
         setIsExpanded(true);
      }
   }, [elementRef]);

   const hasKit = kits.length > 0;

   return (
      <div
         className="flex flex-col gap-0 grow-0 items-start border border-gray-300 rounded overflow-y-hidden md:overflow-y-auto overflow-x-hidden transition-[max-height]"
         style={{
            paddingTop: `${padding}px`,
            paddingLeft: `${padding}px`,
            paddingRight: `${padding}px`,
            paddingBottom: isExpanded ? `${padding}px` : '0px',
            transitionDuration: isExpanded ? '300ms' : undefined,
            maxHeight: isExpanded ? '100%' : `${sectionCollapsedHeight}px`,
         }}
         // @ts-expect-error TS(2322) FIXME: Type 'MutableRefObject<HTMLDivElement | undefined>... Remove this comment to see the full error message
         ref={elementRef}
      >
         {kits.length > 0 && (
            <ProductList
               products={kits}
               heading={___p(kits.length, 'Fix Kit', 'Fix Kits')}
               ItemsAs={FeaturedProduct}
               partOrTool="Kit"
               subheading={___p(
                  kits.length,
                  'This kit contains all the parts and tools needed to complete this guide.',
                  'These kits contain all the parts and tools needed to complete this guide.'
               )}
               renderMicrodata={true}
            />
         )}
         {parts.length > 0 && (
            <ProductList
               products={parts}
               heading={_js('Parts')}
               ItemsAs={Product}
               partOrTool="Part"
               renderMicrodata={!hasKit}
            />
         )}
         {tools.length > 0 && (
            <ProductList
               products={tools}
               heading={_js('Tools')}
               ItemsAs={Product}
               partOrTool="Tool"
               renderMicrodata={!hasKit}
            />
         )}
         <ExpandCollapse
            // @ts-expect-error TS(2322) FIXME: Type 'number | undefined' is not assignable to typ... Remove this comment to see the full error message
            parentPadding={padding}
            buttonHeight={buttonHeight}
            isExpanded={isExpanded}
            // @ts-expect-error TS(2322) FIXME: Type 'Dispatch<boolean>' is not assignable to type... Remove this comment to see the full error message
            setIsExpanded={setIsExpanded}
         />
      </div>
   );
}

function ExpandCollapse({
   buttonHeight,
   parentPadding,
   isExpanded,
   setIsExpanded,
}: {
   buttonHeight: number;
   parentPadding: number;
   isExpanded: boolean;
   setIsExpanded: Dispatch<SetStateAction<boolean>>;
}) {
   const onClick = () => {
      trackAnalyticsCustomEvent({
         eventCategory: 'Guide Products',
         eventAction: 'Guide Products - Show more',
      });
      setIsExpanded(true);
   };

   if (isExpanded) {
      return null;
   }

   return (
      <div
         className="sticky bottom-0 rounded-b p-0 flex flex-col justify-end items-center pointer-events-none"
         style={{
            width: `calc(100% + 2 * ${parentPadding}px)`,
            minHeight: `${buttonHeight}px`,
            marginRight: `-${parentPadding}px`,
            marginLeft: `-${parentPadding}px`,
            marginTop: '1px',
            background:
               'linear-gradient(180deg, rgba(255, 255, 255, 0) 0, rgba(255, 255, 255, 1) 40%, #FFFFFF 100%)',
         }}
      >
         <p
            className="m-0 w-full py-3 text-base text-brand-500 font-medium text-center cursor-pointer pointer-events-auto"
            onClick={onClick}
         >
            {_js('Show more…')}
         </p>
      </div>
   );
}
